import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import UndoRoundedIcon from "@mui/icons-material/Undo";
import { Box, Collapse, Typography } from "@mui/material";
import { type FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AiPromptComposer } from "../../components/UIToolkit/Inputs/AiPromptComposer";
import { AccentIconTile } from "../../components/UIToolkit/molecules/AccentIconTile";
import SystemButton from "../../components/UIToolkit/SystemButton/SystemButton";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { usePrintoutAiSettings } from "../../hooks/usePrintoutAiSettings";
import { FONT_SIZE, ICON_SIZE, INK, MOTION } from "../../theme/tokens";
import type { PrintoutSettings } from "./Header/usePrintout";
import {
	ACCEPTED_EXAMPLE_EXTENSIONS,
	countChangedSettings,
	mergeAiPrintoutSettings,
	PRINTOUT_AI_PROMPT_MAX_LENGTH,
	toAiPrintoutSettings,
} from "./printoutAiSettings";

type Props = {
	/** What the panel is showing right now — both the request's starting point and the undo snapshot. */
	printoutSettings: PrintoutSettings;
	/**
	 * Applies a whole settings object at once (one preview rebuild, one autosave). Bound by the caller
	 * to the printout this panel was rendered for, so an answer that arrives after a switch can't land
	 * on another printout — the caller keys this component by printout, so state never carries over.
	 */
	onApplySettings: (next: PrintoutSettings) => void;
};

/**
 * "Design with Annie" for the Detailed daily printout: the user describes the printout they want (and/or
 * attaches a photo of one), the backend translates it into printout settings, and those land in the
 * panel and the live preview immediately — no confirm step, because the preview beside it IS the
 * confirmation.
 *
 * Applying immediately is only safe because it is reversible: the settings on screen are snapshotted
 * before every apply and an Undo sits next to the summary until the user touches a control themselves
 * (at which point the snapshot no longer describes anything they could want back). That matters more
 * than usual here — the panel autosaves 5s after a change and has no other reset.
 */
export const PrintoutAiAssist: FC<Props> = ({ printoutSettings, onApplySettings }) => {
	const { t } = useTranslation();
	const { track, trackError } = useAnalytics();
	const { showErrorSnackbar } = useHospitalData();
	const { chosenDepartment } = useChosenDepartment();
	const { mutateAsync: configureWithAi, isPending } = usePrintoutAiSettings();

	const [prompt, setPrompt] = useState<string>("");
	const [file, setFile] = useState<File | null>(null);
	const [summary, setSummary] = useState<string | null>(null);
	const [undoSnapshot, setUndoSnapshot] = useState<PrintoutSettings | null>(null);
	/** The settings this component last applied — what is on screen is compared against these. */
	const appliedSettingsRef = useRef<PrintoutSettings | null>(null);
	/**
	 * What the panel is showing *right now*, readable from inside an in-flight request.
	 *
	 * A Bedrock round trip takes seconds and only the composer's submit button is disabled while it
	 * runs, so the user can still toggle a setting mid-flight. Merging the answer onto the settings
	 * captured at submit time would silently discard that toggle (and the undo snapshot would not bring
	 * it back either), so the merge — and the snapshot — are taken against this at completion.
	 */
	const liveSettingsRef = useRef<PrintoutSettings>(printoutSettings);
	liveSettingsRef.current = printoutSettings;

	useEffect(() => {
		const applied = appliedSettingsRef.current;
		// Compared by VALUE, never by identity: the 5s autosave writes the saved settings back through the
		// query cache, which hands this component a brand-new object holding exactly what the AI applied.
		// Keyed on identity that echo looked like a manual edit, and the Undo the whole
		// "apply immediately, no confirm" decision rests on vanished ~5s after every apply, untouched.
		if (applied === null || countChangedSettings(printoutSettings, applied) === 0) return;
		// A real manual change: the snapshot would now restore settings the user has since moved past,
		// and the summary no longer describes the panel.
		appliedSettingsRef.current = null;
		setUndoSnapshot(null);
		setSummary(null);
	}, [printoutSettings]);

	const apply = (next: PrintoutSettings): void => {
		appliedSettingsRef.current = next;
		onApplySettings(next);
	};

	const handleSubmit = async (): Promise<void> => {
		const analyticsBase = {
			department_id: chosenDepartment.department_id,
			has_file: file !== null,
			prompt_length: prompt.trim().length,
		};
		track("printout_ai_configure_start", analyticsBase);
		// The model reasons about the settings as they were when the user asked…
		const askedAbout = printoutSettings;
		try {
			const result = await configureWithAi({ prompt, file, currentSettings: toAiPrintoutSettings(askedAbout) });
			// …but only the model's own changes land, and they land on whatever is on screen when the
			// answer arrives. `result.settings` is the merge of that patch onto the SUBMIT-time
			// settings, so applying it whole would quietly undo a toggle the user flipped while
			// waiting — and Undo, snapshotting the same base, would not give it back.
			const base = liveSettingsRef.current;
			const next = mergeAiPrintoutSettings(base, result.changed);
			setUndoSnapshot(base);
			setSummary(result.summary);
			apply(next);
			setPrompt("");
			setFile(null);
			track("printout_ai_configure_success", { ...analyticsBase, changed_fields_count: countChangedSettings(base, next) });
		} catch (error) {
			track("printout_ai_configure_error", analyticsBase);
			trackError("printout_ai_configure", { ...analyticsBase, error_message: error instanceof Error ? error.message : String(error) });
			showErrorSnackbar(t("printout.ai.failed"), analyticsBase, error);
		}
	};

	const handleUndo = (): void => {
		if (!undoSnapshot) return;
		track("printout_ai_undo", { department_id: chosenDepartment.department_id });
		apply(undoSnapshot);
		setUndoSnapshot(null);
		setSummary(null);
	};

	return (
		<Box data-testid="printout-ai-assist" sx={{ mb: 2 }}>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
				<AccentIconTile size="xs">
					<AutoAwesomeIcon />
				</AccentIconTile>
				<Typography sx={{ color: "primary.dark", fontWeight: 600, fontSize: FONT_SIZE.EMPHASIS }}>{t("printout.ai.title")}</Typography>
			</Box>

			<AiPromptComposer
				collapsible
				maxLength={PRINTOUT_AI_PROMPT_MAX_LENGTH}
				dataTestId="printout-ai-composer"
				value={prompt}
				onChange={setPrompt}
				placeholder={t("printout.ai.placeholder")}
				hint={t("printout.ai.hint")}
				submitLabel={t("printout.ai.submit")}
				onSubmit={() => void handleSubmit()}
				isSubmitting={isPending}
				attachment={{
					accept: ACCEPTED_EXAMPLE_EXTENSIONS,
					file,
					onFileChange: setFile,
					attachLabel: t("printout.ai.attach"),
					removeLabel: t("printout.ai.attachment.remove"),
					invalidTypeLabel: t("printout.ai.attachment.invalid"),
				}}
			/>

			<Collapse in={summary !== null} unmountOnExit>
				<Box
					data-testid="printout-ai-summary"
					aria-live="polite"
					aria-label={t("printout.ai.summary.label")}
					sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, mt: 1 }}
				>
					<Typography sx={{ flex: 1, fontSize: FONT_SIZE.CAPTION, color: INK.MUTED, lineHeight: 1.4 }}>{summary}</Typography>
					{undoSnapshot !== null && (
						<SystemButton
							buttonVariant="borderless"
							size="small"
							onClick={handleUndo}
							data-testid="printout-ai-undo"
							noFullWidthMobile
							leadingIcon={<UndoRoundedIcon sx={{ fontSize: ICON_SIZE.MICRO }} />}
							sx={{
								flexShrink: 0,
								minWidth: 0,
								px: 0.5,
								fontSize: FONT_SIZE.CAPTION,
								// Entering with the summary it belongs to, rather than appearing fully-formed.
								animation: `printout-ai-undo-in ${MOTION.DURATION.ENTRANCE_SM}ms ${MOTION.EASE_OUT} both`,
								"@keyframes printout-ai-undo-in": { from: { opacity: 0, transform: "translateY(-2px)" }, to: { opacity: 1, transform: "none" } },
								"@media (prefers-reduced-motion: reduce)": { animation: "none" },
							}}
						>
							{t("printout.ai.undo")}
						</SystemButton>
					)}
				</Box>
			</Collapse>
		</Box>
	);
};
